'use client'

import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from '@/components/ui/drawer'
import { usePermissions } from '@/features/application/hooks/use-permissions'
import { useIsMobile } from '@/hooks/use-mobile'
import { AnalyticsEvents, trackEvent } from '@/utils/analytics'
import { useSession } from '@/utils/auth/auth-client'
import {
  MOBILE_APP_BANNER_COPY,
  MOBILE_APP_STORE_URLS,
  deriveMobileAppAudience,
  detectMobilePlatform,
  shouldShowMobileAppBanner
} from '@/utils/mobile-app-banner'

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'

import Image from 'next/image'
import { usePathname } from 'next/navigation'

import type { Entity } from '@wci/wv-db-schemas'

const DISMISSED_STORAGE_KEY = 'mobile-app-banner-dismissed-at'

// Same trick as `useIsMobile`: force the hydration render to agree with the
// server (false) by having the two snapshots differ, so React schedules the
// client-only re-render itself instead of this component calling setState
// from an effect just to flip a "mounted" flag.
const subscribeNever = () => () => {}
function useHasMounted(): boolean {
  return useSyncExternalStore(
    subscribeNever,
    () => true,
    () => false
  )
}

function readDismissedAt(): number | null {
  try {
    const raw = window.localStorage.getItem(DISMISSED_STORAGE_KEY)
    if (!raw) return null
    const value = Number(raw)
    return Number.isFinite(value) ? value : null
  } catch {
    // Private browsing / blocked storage — treat as never dismissed. Worse
    // case is the visitor is asked again, which is the safe direction.
    return null
  }
}

function writeDismissedAt(value: number): void {
  try {
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, String(value))
  } catch {
    // Best-effort — a device that blocks storage just gets asked next visit.
  }
}

/**
 * "Get the Atlas app" bottom sheet for phone-width visitors on iOS (non-
 * Safari) or Android. iOS Safari is covered separately by Apple's own Smart
 * App Banner (the `apple-itunes-app` meta tag in the root layout) — see
 * `shouldShowMobileAppBanner` for why this component skips that case rather
 * than stacking a second prompt on top of Apple's.
 *
 * Deliberately renders nothing until mounted: the visibility decision depends
 * on `navigator.userAgent` and `localStorage`, neither available during SSR,
 * and always starting from "hidden" keeps the server and first client
 * render identical so there's no hydration mismatch to paper over.
 */
export function MobileAppBanner() {
  const pathname = usePathname()
  const isPhoneWidth = useIsMobile()
  const { data: session, isPending: sessionPending } = useSession()
  const { can, isLoading: permissionsLoading } = usePermissions()
  // usePermissions() reports loading for as long as there is no user, so a
  // signed-out visitor would wait on it forever. Only a signed-in user has
  // permissions to wait for.
  const isSignedIn = Boolean(session?.user)
  const isLoading = sessionPending || (isSignedIn && permissionsLoading)

  const mounted = useHasMounted()
  // A dismissal that happens THIS render session, so the sheet disappears
  // immediately without waiting on a storage round-trip. The persisted value
  // (from a previous visit) is read straight from localStorage below once
  // mounted, rather than seeded into state — a lazy initializer only ever
  // runs on the first (pre-mount) render, which would freeze it at null.
  const [sessionDismissedAt, setSessionDismissedAt] = useState<number | null>(
    null
  )
  const dismissedAt = sessionDismissedAt ?? (mounted ? readDismissedAt() : null)
  // Captured once at mount — only ever compared against a 14-day window, so
  // being a render (not a wall-clock-exact) timestamp doesn't matter.
  const [mountedAt] = useState(() => Date.now())

  const isInternal = can('internal' as Entity, 'read')
  const hasTechnicianAccess = can('technician' as Entity, 'read')
  const hasPortalAccess = can('portal' as Entity, 'read')

  const accessibleApps = useMemo(() => {
    const apps: string[] = []
    if (hasTechnicianAccess) apps.push('technician')
    if (hasPortalAccess) apps.push('portal')
    return apps
  }, [hasTechnicianAccess, hasPortalAccess])

  const audience = deriveMobileAppAudience({
    isSignedIn,
    isInternal,
    accessibleApps
  })

  const userAgent = mounted ? window.navigator.userAgent : ''
  const platform = detectMobilePlatform(userAgent)

  const visible =
    mounted &&
    !isLoading &&
    platform !== null &&
    shouldShowMobileAppBanner({
      pathname,
      isPhoneWidth,
      userAgent,
      audience,
      dismissedAt,
      now: mountedAt
    })

  useEffect(() => {
    if (visible && platform) {
      trackEvent(AnalyticsEvents.MOBILE_APP_BANNER_SHOWN, {
        audience,
        platform
      })
    }
    // Fires once per transition into `visible`, not on every audience/platform
    // recompute — those can't change while it's already showing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  if (!visible || !platform) return null

  const copy =
    MOBILE_APP_BANNER_COPY[audience as 'technician' | 'internal' | 'signed_out']
  const storeUrl = MOBILE_APP_STORE_URLS[platform]

  const handleDismiss = () => {
    const now = Date.now()
    writeDismissedAt(now)
    setSessionDismissedAt(now)
    trackEvent(AnalyticsEvents.MOBILE_APP_BANNER_DISMISSED, {
      audience,
      platform
    })
  }

  const handleCtaTapped = () => {
    trackEvent(AnalyticsEvents.MOBILE_APP_BANNER_CTA_TAPPED, {
      audience,
      platform
    })
  }

  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open) handleDismiss()
      }}
    >
      <DrawerContent>
        <DrawerHeader className="flex flex-row items-center gap-3 text-left">
          <Image
            src="/atlas/icons/apple-touch-icon.png"
            alt=""
            width={48}
            height={48}
            className="rounded-lg"
          />
          <div>
            <DrawerTitle>Get the Atlas app</DrawerTitle>
            <DrawerDescription>{copy}</DrawerDescription>
          </div>
        </DrawerHeader>
        <DrawerFooter className="flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={handleDismiss}>
            Not now
          </Button>
          <Button asChild className="flex-1" onClick={handleCtaTapped}>
            <a href={storeUrl} target="_blank" rel="noopener noreferrer">
              {platform === 'ios' ? 'App Store' : 'Google Play'}
            </a>
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
